
import React, { useEffect, useState, useCallback } from "react";
import { Shell } from "@/components/layout/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger, TabsGroup } from "@/components/ui/tabs";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { TranslationsManager } from "@/components/admin/TranslationsManager";
import { LogsViewer } from "@/components/admin/LogsViewer";
import { UserManagement } from "@/components/admin/user-management/UserManagement";
import { TestActions } from "@/components/admin/TestActions";
import { EmailSettings } from "@/components/admin/EmailSettings";
import { SeedTranslations } from "@/components/admin/SeedTranslations";
import { BugReportsManager } from "@/components/admin/bug-reports/BugReportsManager";
import { SystemSettings } from "@/components/admin/SystemSettings";
import { FeaturesSettings } from "@/components/admin/FeaturesSettings";
import { CompanyRegisterSettings } from "@/components/admin/CompanyRegisterSettings";
import { WidgetDesigner } from "@/components/admin/widget-designer/WidgetDesigner";
import { SubscriptionsManager } from "@/pages/admin/SubscriptionsManager";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Database, CreditCard, Loader2, RefreshCcw, Shield } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { AdminDebugTool } from "@/components/admin/AdminDebugTool";

export default function AdminPage() {
  const queryClient = useQueryClient();
  const { useHasRoleQuery } = useUserRoles();
  const { data: isDeveloper, isLoading: isLoadingDev, isError: isErrorDev } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin, isLoading: isLoadingAdmin, isError: isErrorAdmin } = useHasRoleQuery("ADMIN");
  const { data: isTester, isLoading: isLoadingTester, isError: isErrorTester } = useHasRoleQuery("TESTER");
  const [activeTab, setActiveTab] = useState("dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  const { getPublicBooleanSetting } = useSystemSettings();
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [permissionError, setPermissionError] = useState(false);
  
  // Track loading states for role checks
  const isLoadingRoles = isLoadingDev || isLoadingAdmin || isLoadingTester;
  const hasError = isErrorDev || isErrorAdmin || isErrorTester;
  
  // Only determine access after loading is complete and there are no errors
  const isFullAdmin = !isLoadingRoles && !hasError && (isDeveloper || isAdmin);
  const hasAdminAccess = !isLoadingRoles && !hasError && (isFullAdmin || isTester);

  // Debug information - write out all role information
  useEffect(() => {
    // Only log once roles are loaded
    if (!isLoadingRoles) {
      console.log("Admin page role check results:", {
        isDeveloper,
        isAdmin,
        isTester,
        isFullAdmin,
        hasAdminAccess,
        errors: { isErrorDev, isErrorAdmin, isErrorTester }
      });
    }
  }, [isDeveloper, isAdmin, isTester, isFullAdmin, hasAdminAccess, isLoadingRoles, isErrorDev, isErrorAdmin, isErrorTester]);

  const handleTabSelect = (value: string) => {
    if (value === "api-integration") {
      navigate("/admin/api-integration");
    } else if (value === "subscriptions") {
      // Show the subscriptions content in the current admin page
      setActiveTab("subscriptions");
    } else {
      setActiveTab(value);
    }
  };

  // Manual retry function for role checks
  const handleRetryRoleChecks = useCallback(() => {
    console.log("Retrying role checks");
    setRetryAttempt(prev => prev + 1);
    // This will trigger query refetch
    queryClient.invalidateQueries({ queryKey: ["hasRole"] });
    setPermissionError(false);
  }, []);
  
  // Set tester's default tab
  useEffect(() => {
    if (!isLoadingRoles && isTester && !isFullAdmin) {
      setActiveTab("bugs");
    }
  }, [isTester, isFullAdmin, isLoadingRoles]);
  
  // Set subscriptions tab as active when returning from subscription plan pages
  useEffect(() => {
    if (location.pathname === "/admin" && location.state?.from === "subscription-plans") {
      setActiveTab("subscriptions");
    }
  }, [location]);

  // Show loading state if role checks are in progress
  if (isLoadingRoles) {
    return (
      <Shell className="p-4 md:p-6">
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-lg font-medium">Loading admin access...</p>
          </div>
        </div>
      </Shell>
    );
  }

  // Show error message if role checks failed
  if (hasError) {
    return (
      <Shell className="p-4 md:p-6">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Checking Permissions</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>
              There was an error checking your admin permissions. This might be a temporary issue.
            </p>
            <Button onClick={handleRetryRoleChecks} className="mt-2">
              <RefreshCcw className="mr-2 h-4 w-4" />
              Retry permission check
            </Button>
          </AlertDescription>
        </Alert>
        
        <div className="mt-6 p-4 border rounded bg-muted/20">
          <h3 className="text-lg font-medium mb-2">Debug Information</h3>
          <p className="text-sm text-muted-foreground mb-2">
            This information may help troubleshoot the issue:
          </p>
          <pre className="text-xs p-2 bg-background rounded overflow-auto">
            {JSON.stringify({
              developer: { result: isDeveloper, error: isErrorDev },
              admin: { result: isAdmin, error: isErrorAdmin },
              tester: { result: isTester, error: isErrorTester },
              retryAttempt
            }, null, 2)}
          </pre>
        </div>
      </Shell>
    );
  }

  // Deny access if user doesn't have admin rights
  if (!hasAdminAccess) {
    return (
      <Shell className="p-4 md:p-6">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to access the admin area. If you believe this is an error, please contact your administrator.
          </AlertDescription>
        </Alert>
        
        <div className="mt-6 p-4 border rounded bg-muted/20">
          <h3 className="text-lg font-medium mb-2">Debug Information</h3>
          <p className="text-sm text-muted-foreground mb-2">
            This information may help troubleshoot the issue:
          </p>
          <pre className="text-xs p-2 bg-background rounded overflow-auto">
            {JSON.stringify({
              developer: isDeveloper,
              admin: isAdmin,
              tester: isTester,
              hasFullAdmin: isFullAdmin,
              hasAdminAccess,
              retryAttempt
            }, null, 2)}
          </pre>
          <Button onClick={handleRetryRoleChecks} className="mt-4">
            <RefreshCcw className="mr-2 h-4 w-4" />
            Retry permission check
          </Button>
        </div>
      </Shell>
    );
  }

  const contentTabs = [
    { label: "Translations", value: "translations" },
    { label: "Email", value: "email" },
    { label: "Logs", value: "logs" },
  ];

  const configTabs = [
    { label: "Tools", value: "tools" },
    { label: "Features", value: "features" },
    { label: "Actions", value: "actions" },
    { label: "Widgets", value: "widgets" },
    { label: "Debug", value: "debug" },
  ];

  return (
    <Shell className="p-4 md:p-6">
      <h1 className="text-3xl font-bold tracking-tight mb-6">
        {isTester && !isFullAdmin ? "Bug Reports" : "Admin Dashboard"}
      </h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="relative mb-6">
          <div className="overflow-x-auto pb-1 scrollbar-none">
            <TabsList className="inline-flex min-w-full">
              {isFullAdmin && (
                <>
                  <TabsTrigger value="dashboard" className="min-w-[100px]">Dashboard</TabsTrigger>
                  <TabsTrigger value="users" className="min-w-[100px]">Users</TabsTrigger>
                  <TabsTrigger 
                    value="subscriptions" 
                    className="min-w-[100px]"
                  >
                    <CreditCard className="mr-2 h-4 w-4" /> Subscriptions
                  </TabsTrigger>
                  <TabsTrigger 
                    value="api-integration" 
                    onClick={() => navigate("/admin/api-integration")} 
                    className="min-w-[100px]"
                  >
                    <Database className="mr-2 h-4 w-4" /> API Integrations
                  </TabsTrigger>
                  <TabsGroup 
                    label="Content" 
                    options={contentTabs}
                    activeTab={activeTab}
                    onSelect={handleTabSelect}
                    className="min-w-[120px]"
                  />
                  <TabsGroup 
                    label="Config" 
                    options={configTabs}
                    activeTab={activeTab}
                    onSelect={handleTabSelect}
                    className="min-w-[120px]"
                  />
                </>
              )}
              <TabsTrigger value="bugs" className="min-w-[100px]">Bug Reports</TabsTrigger>
            </TabsList>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none"></div>
        </div>
        
        {isFullAdmin && (
          <>
            <TabsContent value="dashboard">
              <AdminDashboard />
            </TabsContent>
            
            <TabsContent value="users">
              <UserManagement />
            </TabsContent>
            
            <TabsContent value="subscriptions">
              <SubscriptionsManager />
            </TabsContent>
            
            <TabsContent value="translations">
              <div className="space-y-6">
                <SeedTranslations />
                <TranslationsManager />
              </div>
            </TabsContent>
            
            <TabsContent value="logs">
              <LogsViewer />
            </TabsContent>
            
            <TabsContent value="email">
              <EmailSettings />
            </TabsContent>
            
            <TabsContent value="tools">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="col-span-2">
                  <SystemSettings />
                </div>
                <div className="col-span-2 mt-6">
                  <CompanyRegisterSettings />
                </div>
                <div className="col-span-2 mt-6">
                  <h2 className="text-xl font-semibold mb-4">Additional Tools</h2>
                  <p className="text-muted-foreground">
                    More administrative tools will appear here.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="features">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="col-span-2">
                  <FeaturesSettings />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="actions">
              <TestActions />
            </TabsContent>

            <TabsContent value="widgets">
              <WidgetDesigner />
            </TabsContent>
            
            <TabsContent value="debug">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">Admin Permissions Debug</h2>
                <AdminDebugTool />
              </div>
            </TabsContent>
          </>
        )}
        
        <TabsContent value="bugs">
          <BugReportsManager />
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
